import { useState } from "react";
import { Dialog } from "primereact/dialog";
import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import { Message } from "primereact/message";

import { useInteraction } from "../../contexts/SandboxHooks";

interface SubmitFlagDialogProps {
  visible: boolean;
  onHide: () => void;
}

const SubmitFlagDialog = ({ visible, onHide }: SubmitFlagDialogProps) => {
  const { submitCtfProof } = useInteraction();
  const [flagValue, setFlagValue] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const handleSubmit = async () => {
    if (!flagValue.trim()) return;

    setIsSubmitting(true);
    setErrorMessage("");
    try {
      const result = await submitCtfProof(flagValue);

      if (result?.accepted) {
        // Flag was correct - reset and close
        setFlagValue("");
        onHide();
      } else {
        // Flag was incorrect - show error message
        setErrorMessage("Incorrect flag. Please try again.");
      }
    } catch (error) {
      console.error("Error submitting flag:", error);
      setErrorMessage("An error occurred while submitting the flag. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setFlagValue("");
    setErrorMessage("");
    onHide();
  };

  return (
    <Dialog
      header="Submit Flag"
      visible={visible}
      onHide={handleClose}
      style={{ width: "32rem" }}
      modal
      data-testid="submit-flag-dialog"
      footer={
        <div className="flex gap-2 justify-end">
          <Button
            label="Cancel"
            severity="secondary"
            data-testid="submit-flag-cancel-button"
            onClick={handleClose}
            disabled={isSubmitting}
          />
          <Button
            label="Submit"
            icon="pi pi-check"
            data-testid="submit-flag-submit-button"
            disabled={!flagValue.trim() || isSubmitting}
            loading={isSubmitting}
            onClick={handleSubmit}
          />
        </div>
      }
    >
      <div className="flex flex-col gap-3">
        <p className="m-0">
          Follow the challenge instructions to find the flag. Once you’ve found it, paste it below
          and click <em>Submit</em>.
        </p>
        <InputText
          value={flagValue}
          onChange={e => setFlagValue(e.target.value)}
          placeholder="Enter flag..."
          className="w-full"
          data-testid="submit-flag-input"
          autoFocus
          onKeyDown={e => {
            if (e.key === "Enter" && flagValue.trim() && !isSubmitting) {
              handleSubmit();
            }
          }}
        />
        {errorMessage && <Message severity="error" text={errorMessage} className="w-full" />}
      </div>
    </Dialog>
  );
};

export default SubmitFlagDialog;
